import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

export type AppMode = 'business' | 'family';

const STORAGE_KEY = 'tax-tracker-mode';

interface ModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  isFamily: boolean;
}

const ModeContext = createContext<ModeContextType | undefined>(undefined);

export function ModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AppMode>(() => {
    if (typeof window === 'undefined') return 'business';
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'family' ? 'family' : 'business';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const setMode = useCallback((next: AppMode) => {
    setModeState(next);
  }, []);

  return (
    <ModeContext.Provider value={{ mode, setMode, isFamily: mode === 'family' }}>
      {children}
    </ModeContext.Provider>
  );
}

export function useMode(): ModeContextType {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error('useMode must be used within ModeProvider');
  return ctx;
}
